import { ChevronDown } from "lucide-react"
import { forwardRef, useState } from "react"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib"

export interface SelectOption {
  value: string
  label: string
}

interface CustomSelectProps {
  label: string
  error?: string
  onClearError?: () => void
  options: SelectOption[]
  required?: boolean
  placeholder?: string
  layout?: "vertical" | "horizontal"
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onValueChange?: (value: string) => void
  disabled?: boolean
  className?: string
  id?: string
}

export const CustomSelect = forwardRef<HTMLSelectElement, CustomSelectProps>(
  (
    {
      label,
      error,
      className = "",
      onClearError,
      required,
      options,
      placeholder,
      layout = "vertical",
      value,
      onChange,
      onValueChange,
      disabled,
      id,
    },
    _ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)

    // Handler that supports both onChange and onValueChange
    const handleValueChange = (val: string) => {
      onClearError?.()
      onValueChange?.(val)
      // Also call onChange for backward compatibility with react-hook-form
      if (onChange) {
        const event = {
          target: { value: val },
        } as React.ChangeEvent<HTMLSelectElement>
        onChange(event)
      }
    }

    // Horizontal layout - similar to MultiSelectDropdown
    if (layout === "horizontal") {
      return (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-0">
          {/* Label */}
          <span className="text-[16px] text-body flex-1">
            {label}
            {required && (
              <span className={cn("ms-1", error ? "text-brand-terracotta-bright" : "text-body")}>
                *
              </span>
            )}
          </span>

          {/* Select Dropdown */}
          <div className="relative md:min-w-[200px]">
            <Select
              value={value}
              onValueChange={handleValueChange}
              open={isOpen}
              onOpenChange={setIsOpen}
              disabled={disabled}
            >
              <SelectTrigger
                id={id}
                className={cn(
                  "font-bold rounded-lg text-[14px] text-primary text-right focus:outline-none transition-all duration-200 bg-transparent cursor-pointer pr-6 border-b-2 [&>svg]:hidden",
                  error
                    ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                    : "border-input",
                  disabled && "opacity-50 cursor-not-allowed",
                )}
              >
                <SelectValue placeholder={placeholder || "Select..."} />
              </SelectTrigger>
              <SelectContent>
                {options.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none">
              <ChevronDown className="size-4" />
            </div>
          </div>

          {error && <p className="mt-1 text-xs text-brand-terracotta-bright w-full">{error}</p>}
        </div>
      )
    }

    // Vertical layout (default)
    return (
      <div className="relative flex flex-col gap-1.25">
        <Label
          className={cn(
            "text-xs font-normal leading-5 uppercase flex gap-1",
            error ? "text-brand-terracotta-bright" : "text-body",
          )}
        >
          {label}
          {required && (
            <span className={error ? "text-brand-terracotta-bright" : "text-body"}>*</span>
          )}
        </Label>
        <div className="relative">
          <Select
            value={value}
            onValueChange={handleValueChange}
            open={isOpen}
            onOpenChange={setIsOpen}
            disabled={disabled}
          >
            <SelectTrigger
              id={id}
              className={cn(
                "w-full pb-2.5 pt-0 ps-0 !h-7.5 rounded-none bg-transparent text-sm leading-5 font-semibold appearance-none focus:outline-none transition-all duration-200 border-b-1 pr-8 [&>svg]:hidden",
                error
                  ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                  : "border-input text-[var(--color-chat-text)]",
                disabled && "opacity-50 cursor-not-allowed",
                className,
              )}
            >
              <SelectValue placeholder={placeholder || "Select"} />
            </SelectTrigger>
            <SelectContent className="absolute w-100 left-5">
              {options.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none">
            <ChevronDown className="size-5" />
          </div>
        </div>
        {error && <p className="mt-1 text-xs text-brand-terracotta-bright">{error}</p>}
      </div>
    )
  },
)

CustomSelect.displayName = "CustomSelect"
